'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

export interface UserRole {
  id: number;
  name: string;
  display_name_ar: string;
  display_name_en: string;
}

export interface UserPosition {
  id: number;
  position_name?: string;
  display_name_ar?: string;
  display_name_en?: string;
  department?: string;
  academic_year?: string;
  notes?: string;
}

export interface UserSchool {
  id: number;
  name: string;
  code: string;
}

export interface AuthUser {
  id: number;
  username: string;
  full_name: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  school?: UserSchool;
  roles: UserRole[];
  permissions: string[];
  positions: UserPosition[];
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isTeacher: boolean;
  isStaff: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string | string[], requireAll?: boolean) => boolean;
  hasRole: (roleName: string | string[]) => boolean;
  hasPosition: (positionName: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const safetyTimer = setTimeout(() => {
      if (isMounted) setIsLoading(false);
    }, 2500);

    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('school_saas_token');
        const savedUser = localStorage.getItem('school_saas_user');

        if (savedToken && savedToken !== 'undefined' && savedToken !== 'null' && savedToken.trim().length > 0) {
          setToken(savedToken);
          if (savedUser && savedUser !== 'undefined' && savedUser !== 'null' && savedUser.trim().length > 0) {
            try {
              setUser(JSON.parse(savedUser));
            } catch {
              localStorage.removeItem('school_saas_user');
              setUser(null);
            }
          }

          // Verify token with backend
          try {
            const res = await api.get('/auth/me');
            if (res?.user && isMounted) {
              setUser(res.user);
              localStorage.setItem('school_saas_user', JSON.stringify(res.user));
            } else if (isMounted) {
              throw new Error('No user returned');
            }
          } catch {
            if (isMounted) {
              localStorage.removeItem('school_saas_token');
              localStorage.removeItem('school_saas_user');
              setToken(null);
              setUser(null);
            }
          }
        } else {
          if (isMounted) {
            localStorage.removeItem('school_saas_token');
            localStorage.removeItem('school_saas_user');
            setToken(null);
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        if (isMounted) {
          clearTimeout(safetyTimer);
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
    };
  }, []);

  const login = async (username: string, password: string) => {
    const data = await api.post('/auth/login', { username, password });
    if (data?.token && data?.user) {
      localStorage.setItem('school_saas_token', data.token);
      localStorage.setItem('school_saas_user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await api.post('/auth/logout');
      }
    } catch {
      // Ignore network errors during logout
    } finally {
      localStorage.removeItem('school_saas_token');
      localStorage.removeItem('school_saas_user');
      setToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      if (res?.user) {
        setUser(res.user);
        localStorage.setItem('school_saas_user', JSON.stringify(res.user));
      }
    } catch {
      // Silent error
    }
  };

  // Permission Evaluation
  const hasPermission = useCallback(
    (permission: string | string[], requireAll = false): boolean => {
      if (!user) return false;

      // Admin has blanket access to everything
      const userRoles = user.roles?.map((r) => r.name) || [];
      if (userRoles.includes('admin')) {
        return true;
      }

      const userPermissions = user.permissions || [];
      const required = Array.isArray(permission) ? permission : [permission];

      if (requireAll) {
        return required.every((p) => userPermissions.includes(p));
      }
      return required.some((p) => userPermissions.includes(p));
    },
    [user]
  );

  // Role Evaluation
  const hasRole = useCallback(
    (roleName: string | string[]): boolean => {
      if (!user) return false;
      const userRoles = user.roles?.map((r) => r.name) || [];
      const required = Array.isArray(roleName) ? roleName : [roleName];
      return required.some((r) => userRoles.includes(r));
    },
    [user]
  );

  // Position / School Responsibility Evaluation
  const hasPosition = useCallback(
    (positionName: string | string[]): boolean => {
      if (!user || !user.positions) return false;
      const required = Array.isArray(positionName) ? positionName : [positionName];
      return user.positions.some((pos) => {
        const pName = (pos.position_name || '').toLowerCase();
        const pAr = (pos.display_name_ar || '').toLowerCase();
        return required.some(
          (req) => req.toLowerCase() === pName || req.toLowerCase() === pAr
        );
      });
    },
    [user]
  );

  const isAdmin = !!user?.roles?.some((r) => r.name === 'admin');
  const isTeacher = !!user?.roles?.some((r) => r.name === 'teacher');
  const isStaff = !!user?.roles?.some((r) => r.name === 'staff');

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user && !!token,
        isLoading,
        isAdmin,
        isTeacher,
        isStaff,
        login,
        logout,
        refreshUser,
        hasPermission,
        hasRole,
        hasPosition,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
